import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Loader2, X, ChevronLeft, ChevronRight, Folder } from "lucide-react";
import { fetchPublicProject } from "../lib/portfolio";
import ShareButton from "../components/portfolio/ShareButton";

/* /portfolio/project/:id — public detail page for a grouped project.
 * Shows name, description, tags, a media grid, and a lightbox carousel. */
const PortfolioProject = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightboxIdx, setLightboxIdx] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const d = await fetchPublicProject(id);
        if (cancelled) return;
        setProject(d);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Project not found");
        if (e?.response?.status === 404) navigate("/portfolio");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id, navigate]);

  const media = project?.media || [];
  const closeLightbox = useCallback(() => setLightboxIdx(null), []);
  const prevLightbox = useCallback(() => {
    setLightboxIdx((i) => (i == null ? null : (i - 1 + media.length) % media.length));
  }, [media.length]);
  const nextLightbox = useCallback(() => {
    setLightboxIdx((i) => (i == null ? null : (i + 1) % media.length));
  }, [media.length]);

  useEffect(() => {
    if (lightboxIdx == null) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeLightbox();
      else if (e.key === "ArrowLeft") prevLightbox();
      else if (e.key === "ArrowRight") nextLightbox();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightboxIdx, closeLightbox, prevLightbox, nextLightbox]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-[color:var(--ups-subtext)]">
        <Loader2 className="animate-spin mr-2" /> Loading…
      </div>
    );
  }
  if (!project) return null;

  return (
    <div className="min-h-screen bg-[color:var(--ups-paper)]" data-testid="portfolio-project-page">
      {/* Hero */}
      <header className="border-b-2" style={{ borderColor: "var(--ups-ink)", background: "#fff" }}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
          <button
            onClick={() => navigate("/portfolio")}
            className="text-[11px] font-bold uppercase tracking-[0.18em] flex items-center gap-1.5 hover:underline mb-3"
            data-testid="back-to-portfolio"
          >
            <ArrowLeft size={12} /> Portfolio
          </button>
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[color:var(--ups-subtext)] flex items-center gap-1.5">
                <Folder size={12} /> Project · {project.media_count} {project.media_count === 1 ? "item" : "items"}
              </div>
              <h1 className="font-display text-4xl sm:text-5xl font-bold mt-1" data-testid="project-name">{project.name}</h1>
              {project.description && (
                <p className="mt-2 text-[14px] leading-relaxed max-w-2xl text-[color:var(--ups-subtext)]" data-testid="project-description">
                  {project.description}
                </p>
              )}
              <div className="mt-3 flex items-center gap-2 flex-wrap">
                {(project.product_tags || []).map((t) => (
                  <span key={t} className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.14em] rounded-sm" style={{ background: "var(--ups-muted)", border: "1px solid var(--ups-border)" }}>
                    {t}
                  </span>
                ))}
                {(project.industries || []).map((s) => (
                  <button key={s} onClick={() => navigate(`/portfolio/${s}`)} className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.14em] rounded-sm hover:bg-white" style={{ background: "var(--ups-gold)", border: "1px solid var(--ups-ink)" }}>
                    {s}
                  </button>
                ))}
              </div>
            </div>
            <ShareButton url={`${window.location.origin}/portfolio/project/${project.id}`} label={project.name} />
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
        {media.length === 0 ? (
          <div className="py-16 text-center text-sm text-[color:var(--ups-subtext)] border-2 border-dashed rounded-sm" style={{ borderColor: "var(--ups-border)" }}>
            No media in this project yet.
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="project-media-grid">
            {media.map((m, i) => (
              <button
                key={m.id}
                onClick={() => setLightboxIdx(i)}
                className="relative group block aspect-square bg-stone-200 overflow-hidden border-2 hover:-translate-y-0.5 transition-transform"
                style={{ borderColor: "var(--ups-ink)" }}
                data-testid={`project-media-${m.id}`}
                aria-label={m.caption || m.filename || "Media"}
              >
                {m.media_type === "video" ? (
                  <>
                    <video src={m.image_url} muted playsInline preload="metadata" className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: "rgba(0,0,0,0.55)" }}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="#fff"><path d="M8 5v14l11-7z"/></svg>
                      </div>
                    </div>
                  </>
                ) : (
                  <img src={m.thumb_url || m.image_url} alt={m.caption || "Print work"} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform" referrerPolicy="no-referrer" />
                )}
              </button>
            ))}
          </div>
        )}
      </main>

      {lightboxIdx != null && media[lightboxIdx] && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.9)" }} onClick={closeLightbox} data-testid="lightbox">
          <button onClick={(e) => { e.stopPropagation(); closeLightbox(); }} className="absolute top-4 right-4 text-white p-2 hover:bg-white/10 rounded-sm" aria-label="Close">
            <X size={20} />
          </button>
          {media.length > 1 && (
            <>
              <button onClick={(e) => { e.stopPropagation(); prevLightbox(); }} className="absolute left-4 top-1/2 -translate-y-1/2 text-white p-2 hover:bg-white/10 rounded-sm" aria-label="Previous">
                <ChevronLeft size={28} />
              </button>
              <button onClick={(e) => { e.stopPropagation(); nextLightbox(); }} className="absolute right-4 top-1/2 -translate-y-1/2 text-white p-2 hover:bg-white/10 rounded-sm" aria-label="Next">
                <ChevronRight size={28} />
              </button>
            </>
          )}
          {media[lightboxIdx].media_type === "video" ? (
            <video src={media[lightboxIdx].image_url} controls autoPlay playsInline className="max-w-[92vw] max-h-[88vh]" onClick={(e) => e.stopPropagation()} />
          ) : (
            <img src={media[lightboxIdx].image_url} alt={media[lightboxIdx].caption || ""} className="max-w-[92vw] max-h-[88vh] object-contain" onClick={(e) => e.stopPropagation()} referrerPolicy="no-referrer" />
          )}
        </div>
      )}
    </div>
  );
};

export default PortfolioProject;
